import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  ArrowLeft, Building2, MapPin, Plus, X, Save,
  Check, RefreshCw, Globe, History, Layers, Flame, ClipboardList, ShieldCheck, Scale
} from 'lucide-react'
import {
  getRanches, getRanch, updateRanch, getPaddockConfigurations,
  getBiocharSettings, updateBiocharSettings,
  getOperatingProfile, updateOperatingProfile,
  getFinanceApprovalSettings, updateFinanceApprovalSettings,
  getSmartKraalFarm, updateSmartKraalFarm,
} from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'
import { isSystemAdmin } from '../../lib/permissions'

export default function RanchSettings({ currentUser, currentRanchId, onRanchChange }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { role } = useAuth()
  
  const [isEditing, setIsEditing] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  
  const [coordinates, setCoordinates] = useState([])
  const [areaHa, setAreaHa] = useState('')

  // Fetch all ranches (for switching)
  const { data: ranches } = useQuery({
    queryKey: ['ranches'],
    queryFn: getRanches,
  })

  // Fetch current ranch details
  const { data: ranch, isLoading } = useQuery({
    queryKey: ['ranch', currentRanchId],
    queryFn: () => getRanch(currentRanchId),
    enabled: !!currentRanchId,
  })

  // Initialize form when ranch loads
  useEffect(() => {
    if (ranch) {
      const coords = ranch.boundary_coordinates || []
      setCoordinates(coords.length > 0 ? coords : [
        { lat: '', lng: '' },
        { lat: '', lng: '' },
        { lat: '', lng: '' },
      ])
      setAreaHa(ranch.area_ha || '')
    }
  }, [ranch])

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data) => updateRanch(currentRanchId, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['ranch', currentRanchId])
      setSuccess('Ranch boundaries updated successfully!')
      setIsEditing(false)
      setTimeout(() => setSuccess(''), 3000)
    },
    onError: (err) => {
      setError(err.response?.data?.detail || 'Failed to update ranch')
    },
  })

  // Calculate area from coordinates
  const calculatePolygonArea = (coords) => {
    const validCoords = coords.filter(c => c.lat && c.lng)
    if (validCoords.length < 3) return null

    const points = validCoords.map(c => ({
      lat: parseFloat(c.lat),
      lng: parseFloat(c.lng)
    }))

    const n = points.length
    let area = 0
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n
      area += points[i].lng * points[j].lat
      area -= points[j].lng * points[i].lat
    }
    area = Math.abs(area) / 2

    const metersPerDegree = 111320
    const areaM2 = area * metersPerDegree * metersPerDegree
    return areaM2 / 10000
  }

  const addCoordinate = () => {
    setCoordinates([...coordinates, { lat: '', lng: '' }])
  }

  const removeCoordinate = (index) => {
    if (coordinates.length > 3) {
      setCoordinates(coordinates.filter((_, i) => i !== index))
    }
  }

  const updateCoordinate = (index, field, value) => {
    const newCoords = [...coordinates]
    newCoords[index] = { ...newCoords[index], [field]: value }
    setCoordinates(newCoords)
  }

  const handleSave = () => {
    setError('')
    
    const validCoords = coordinates.filter(c => c.lat && c.lng)
    if (validCoords.length < 3) {
      setError('Please enter at least 3 coordinate points')
      return
    }

    // Convert string values to numbers
    const numericCoords = validCoords.map(c => ({
      lat: parseFloat(c.lat),
      lng: parseFloat(c.lng)
    }))

    const calculatedArea = calculatePolygonArea(numericCoords)

    updateMutation.mutate({
      boundary_coordinates: numericCoords,
      area_ha: areaHa ? parseFloat(areaHa) : calculatedArea,
    })
  }

  const handleRecalculateArea = () => {
    const area = calculatePolygonArea(coordinates)
    if (area) {
      setAreaHa(area.toFixed(2))
    }
  }

  const hasCoordinates = ranch?.boundary_coordinates && ranch.boundary_coordinates.length >= 3

  if (isLoading) {
    return <div className="p-6 text-center">Loading ranch details...</div>
  }

  return (
    <div className="max-w-3xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">Ranch Settings</h1>
          <p className="text-gray-600">Manage ranch boundaries and settings</p>
        </div>
      </div>

      {/* Ranch Switcher (if multiple ranches) */}
      {ranches && ranches.length > 1 && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Globe className="h-5 w-5 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">Switch Ranch</h2>
          </div>
          
          <div className="flex flex-wrap gap-2">
            {ranches.map((r) => (
              <button
                key={r.id}
                onClick={() => onRanchChange && onRanchChange(r.id)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  r.id === currentRanchId
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {r.name}
                {r.id === currentRanchId && <Check className="inline ml-2 h-4 w-4" />}
              </button>
            ))}
          </div>
          
          <p className="mt-3 text-sm text-gray-500">
            Click a ranch to switch. Current: <strong>{ranch?.name}</strong>
          </p>
        </div>
      )}

      {success && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 text-green-700 rounded-lg flex items-center gap-2">
          <Check className="h-5 w-5" />
          {success}
        </div>
      )}

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      {/* Ranch Info */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Building2 className="h-5 w-5 text-green-600" />
          <h2 className="text-lg font-semibold text-gray-900">Ranch Information</h2>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-sm text-gray-500">Code</p>
            <p className="font-medium">{ranch?.code}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Name</p>
            <p className="font-medium">{ranch?.name}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Area</p>
            <p className="font-medium">{ranch?.area_ha ? `${ranch.area_ha} ha` : 'Not set'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Currency</p>
            <p className="font-medium">{ranch?.currency || 'UGX'}</p>
          </div>
        </div>
      </div>

      {/* Boundary Status */}
      {!hasCoordinates && !isEditing && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <h3 className="font-medium text-yellow-900 mb-2">⚠️ No Boundary Coordinates</h3>
          <p className="text-sm text-yellow-800 mb-3">
            This ranch doesn't have boundary coordinates set. Without boundaries, 
            you won't be able to validate that paddocks are within the ranch area.
          </p>
          <button
            onClick={() => setIsEditing(true)}
            className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 text-sm"
          >
            Add Boundary Coordinates
          </button>
        </div>
      )}

      {/* Boundary Coordinates */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">Ranch Boundary</h2>
          </div>
          {!isEditing && hasCoordinates && (
            <button
              onClick={() => setIsEditing(true)}
              className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
            >
              Edit Boundary
            </button>
          )}
        </div>

        {isEditing ? (
          <>
            <p className="text-sm text-gray-600 mb-4">
              Enter GPS coordinate points that define the ranch perimeter. 
              Paddocks must have all their points within this boundary.
            </p>

            <div className="space-y-3 mb-4">
              {coordinates.map((coord, index) => (
                <div key={index} className="flex items-center gap-2">
                  <span className="text-sm text-gray-500 w-8">#{index + 1}</span>
                  <input
                    type="number"
                    step="any"
                    placeholder="Latitude"
                    value={coord.lat}
                    onChange={(e) => updateCoordinate(index, 'lat', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  />
                  <input
                    type="number"
                    step="any"
                    placeholder="Longitude"
                    value={coord.lng}
                    onChange={(e) => updateCoordinate(index, 'lng', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  />
                  {coordinates.length > 3 && (
                    <button
                      type="button"
                      onClick={() => removeCoordinate(index)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>

            <button
              type="button"
              onClick={addCoordinate}
              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 mb-4"
            >
              <Plus className="h-4 w-4" />
              Add Point
            </button>

            {/* Area */}
            <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg mb-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Area (hectares)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={areaHa}
                  onChange={(e) => setAreaHa(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  placeholder="Calculated from coordinates"
                />
              </div>
              <button
                type="button"
                onClick={handleRecalculateArea}
                className="flex items-center gap-1 px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 mt-6"
              >
                <RefreshCw className="h-4 w-4" />
                Calculate
              </button>
            </div>

            {/* Action buttons */}
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setIsEditing(false)
                  // Reset to original values
                  if (ranch) {
                    setCoordinates(ranch.boundary_coordinates || [])
                    setAreaHa(ranch.area_ha || '')
                  }
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={updateMutation.isPending}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                <Save className="h-4 w-4" />
                {updateMutation.isPending ? 'Saving...' : 'Save Boundary'}
              </button>
            </div>
          </>
        ) : hasCoordinates ? (
          <>
            <p className="text-sm text-gray-600 mb-4">
              {ranch.boundary_coordinates.length} coordinate points define this ranch's boundary.
            </p>
            
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                {ranch.boundary_coordinates.map((coord, index) => (
                  <div key={index} className="p-2 bg-white rounded border">
                    <span className="text-gray-500">#{index + 1}:</span>{' '}
                    <span className="font-mono text-xs">
                      {Number(coord.lat)?.toFixed(4)}, {Number(coord.lng)?.toFixed(4)}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-800">
                ✓ Ranch boundary is set. New paddocks will be validated against this boundary.
              </p>
            </div>
          </>
        ) : (
          <p className="text-gray-500 text-center py-4">
            No boundary coordinates set. Click "Add Boundary Coordinates" above.
          </p>
        )}
      </div>

      {/* Operating Profile */}
      <OperatingProfileConfig ranchId={currentRanchId} />

      {/* SmartKraal farm link (platform setting: system admin only) */}
      {isSystemAdmin(role) && <SmartKraalFarmConfig ranchId={currentRanchId} />}

      {/* Expense / finance approval settings (owners govern; managers view) */}
      <FinanceApprovalConfig ranchId={currentRanchId} />

      {/* Biochar Configuration */}
      <BiocharConfig ranchId={currentRanchId} />

      {/* Paddock Configuration History */}
      <ConfigurationHistory ranchId={currentRanchId} />

      {/* Create New Ranch Link */}
      <div className="mt-6 text-center">
        <button
          onClick={() => navigate('/ranch/setup')}
          className="text-green-600 hover:text-green-700 text-sm"
        >
          + Create a new ranch
        </button>
      </div>
    </div>
  )
}

const CADENCE_FIELDS = [
  { key: 'dipping', label: 'Dipping / spraying', hint: 'e.g. every 14 days' },
  { key: 'deworming', label: 'Deworming', hint: 'e.g. every 90 days' },
  { key: 'vaccination', label: 'Vaccination drives', hint: 'e.g. every 180 days' },
]

function OperatingProfileConfig({ ranchId }) {
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [capacity, setCapacity] = useState('')
  const [cadences, setCadences] = useState({})
  const [pregFreq, setPregFreq] = useState('1')
  const [baseline, setBaseline] = useState('')   // census day 0 (4b-2): "on the farm since"

  const { data: profile, isLoading } = useQuery({
    queryKey: ['operatingProfile', ranchId],
    queryFn: () => getOperatingProfile(ranchId),
    enabled: !!ranchId,
  })

  useEffect(() => {
    if (profile) {
      setCapacity(profile.weekly_weighing_capacity != null ? String(profile.weekly_weighing_capacity) : '')
      setCadences(profile.campaign_cadences || {})
      setPregFreq(profile.preg_test_frequency_per_year != null ? String(profile.preg_test_frequency_per_year) : '1')
      setBaseline(profile.herd_baseline_date || '')
    }
  }, [profile])

  const saveMutation = useMutation({
    mutationFn: (data) => updateOperatingProfile(ranchId, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['operatingProfile', ranchId])
      setEditing(false)
      setSaveError('')
      setSaveSuccess(true)
      setTimeout(() => setSaveSuccess(false), 3000)
    },
    onError: (err) => {
      setSaveError(err.response?.data?.detail || 'Failed to save operating profile')
    },
  })

  const handleSave = () => {
    const payload = {
      campaign_cadences: Object.fromEntries(
        CADENCE_FIELDS
          .map(({ key }) => [key, parseInt(cadences[key], 10)])
          .filter(([, days]) => Number.isFinite(days) && days > 0)
      ),
    }
    const cap = parseInt(capacity, 10)
    if (Number.isFinite(cap) && cap > 0) payload.weekly_weighing_capacity = cap
    const pf = parseInt(pregFreq, 10)
    if (Number.isFinite(pf) && pf >= 0) payload.preg_test_frequency_per_year = pf
    if (/^\d{4}-\d{2}-\d{2}$/.test(baseline)) payload.herd_baseline_date = baseline
    saveMutation.mutate(payload)
  }

  if (isLoading) return null

  return (
    <div className="bg-white rounded-lg shadow p-6 mt-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ClipboardList className="h-5 w-5 text-green-700" />
          <h2 className="text-lg font-semibold text-gray-900">Operating Profile</h2>
        </div>
        {!editing && (
          <button
            onClick={() => setEditing(true)}
            className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
          >
            Edit
          </button>
        )}
      </div>

      <p className="text-sm text-gray-500 mb-4">
        The farm's stated operating plan. Reports and alerts judge activity against this plan —
        e.g. a rotating weekly weighing sample is expected, not flagged, once capacity is set here.
      </p>

      {saveSuccess && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg flex items-center gap-2 text-sm">
          <Check className="h-4 w-4" />
          Operating profile saved
        </div>
      )}
      {saveError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
          {saveError}
        </div>
      )}

      {editing ? (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Weekly weighing capacity (animals/week)
            </label>
            <input
              type="number"
              min="1"
              step="1"
              value={capacity}
              onChange={(e) => setCapacity(e.target.value)}
              className="w-full sm:w-48 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              placeholder="e.g., 200"
            />
            <p className="text-xs text-gray-500 mt-1">
              How many animals can realistically go through the crush per week.
            </p>
          </div>

          <div>
            <p className="block text-sm font-medium text-gray-700 mb-1">Routine campaign schedule (days between)</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {CADENCE_FIELDS.map(({ key, label, hint }) => (
                <div key={key}>
                  <label className="block text-xs text-gray-600 mb-1">{label}</label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={cadences[key] ?? ''}
                    onChange={(e) => setCadences({ ...cadences, [key]: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    placeholder={hint}
                  />
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Leave blank to skip overdue tracking for that campaign type.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Pregnancy tests per year
            </label>
            <input
              type="number"
              min="0"
              max="4"
              step="1"
              value={pregFreq}
              onChange={(e) => setPregFreq(e.target.value)}
              className="w-full sm:w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="herd-baseline-date">
              Herd baseline date (census day 0)
            </label>
            <input
              id="herd-baseline-date"
              type="date"
              value={baseline}
              onChange={(e) => setBaseline(e.target.value)}
              className="w-full sm:w-48 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              data-testid="herd-baseline-date"
            />
            <p className="text-xs text-gray-500 mt-1">
              Animals registered in census mode at the crush are on the farm since this date: opening stock in the
              weekly reconciliation, not that week's purchases. Set it once, before the census day.
            </p>
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => {
                setEditing(false)
                setSaveError('')
                setCapacity(profile?.weekly_weighing_capacity != null ? String(profile.weekly_weighing_capacity) : '')
                setCadences(profile?.campaign_cadences || {})
                setPregFreq(profile?.preg_test_frequency_per_year != null ? String(profile.preg_test_frequency_per_year) : '1')
                setBaseline(profile?.herd_baseline_date || '')
              }}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saveMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-green-700 text-white rounded-lg hover:bg-green-800 disabled:opacity-50 text-sm"
            >
              <Save className="h-4 w-4" />
              {saveMutation.isPending ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      ) : (
        <div className="p-4 bg-gray-50 rounded-lg">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-gray-500">Weekly weighing capacity</p>
              <p className="font-medium text-gray-900">
                {profile?.weekly_weighing_capacity ? `${profile.weekly_weighing_capacity} / week` : 'Not set'}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Campaign schedule</p>
              <p className="font-medium text-gray-900">
                {profile?.campaign_cadences && Object.keys(profile.campaign_cadences).length > 0
                  ? Object.entries(profile.campaign_cadences)
                      .map(([k, v]) => `${k} every ${v}d`)
                      .join(' · ')
                  : 'Not set'}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Pregnancy tests / year</p>
              <p className="font-medium text-gray-900">
                {profile?.preg_test_frequency_per_year ?? 'Not set'}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Herd baseline date (census day 0)</p>
              <p className="font-medium text-gray-900" data-testid="herd-baseline-date-view">
                {profile?.herd_baseline_date || 'Not set'}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const POLICY_LABELS = {
  first: 'First approver decides (any one designated owner)',
  majority: 'Majority of designated approvers',
  unanimous: 'All designated approvers',
}

/**
 * Which SmartKraal farm id this ranch owns (smartkraal.farms, migration 137).
 * System admin only: it names the key a Cloud Run job reads and decides which
 * ranch a kraal's readings land on. The key itself is never shown or entered
 * here — only the env var the sync job expects it in.
 */
function SmartKraalFarmConfig({ ranchId }) {
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [farmId, setFarmId] = useState('')
  const [enabled, setEnabled] = useState(true)
  const [floor, setFloor] = useState('')
  const [notes, setNotes] = useState('')
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [saveError, setSaveError] = useState('')

  const { data, isLoading, error } = useQuery({
    queryKey: ['smartkraalFarm', ranchId],
    queryFn: ({ signal }) => getSmartKraalFarm(ranchId, { signal }),
    enabled: !!ranchId,
  })
  const farm = data?.farm

  useEffect(() => {
    setFarmId(farm?.farm_id != null ? String(farm.farm_id) : '')
    setEnabled(farm ? !!farm.enabled : true)
    setFloor(farm?.min_confidence_promote != null ? String(farm.min_confidence_promote) : '')
    setNotes(farm?.notes || '')
  }, [farm])

  const saveMutation = useMutation({
    mutationFn: (body) => updateSmartKraalFarm(ranchId, body),
    onSuccess: () => {
      queryClient.invalidateQueries(['smartkraalFarm', ranchId])
      queryClient.invalidateQueries(['smartkraalHealth', ranchId])
      setEditing(false)
      setSaveError('')
      setSaveSuccess(true)
      setTimeout(() => setSaveSuccess(false), 3000)
    },
    onError: (err) => setSaveError(err.response?.data?.detail || 'Could not save the SmartKraal farm'),
  })

  const handleSave = () => {
    const id = parseInt(farmId, 10)
    if (!id || id <= 0) { setSaveError('Enter the SmartKraal farm id (a_idfarm) the kraal reports under.'); return }
    const f = floor.trim() === '' ? null : parseInt(floor, 10)
    if (f != null && (Number.isNaN(f) || f < 0 || f > 100)) { setSaveError('The promotion floor is a confidence from 0 to 100, or blank.'); return }
    saveMutation.mutate({ farm_id: id, enabled, min_confidence_promote: f, notes: notes.trim() || null })
  }

  if (isLoading) return null

  return (
    <div className="bg-white rounded-lg shadow p-6 mt-6" data-testid="smartkraal-farm-config">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Scale className="h-5 w-5 text-primary-700" />
          <h2 className="text-lg font-semibold text-gray-900">SmartKraal</h2>
          <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600">system admin</span>
        </div>
        {!editing && (
          <button onClick={() => setEditing(true)} className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">
            {farm ? 'Edit' : 'Link a kraal'}
          </button>
        )}
      </div>
      <p className="text-sm text-gray-600 mb-4">
        Which SmartKraal farm id this ranch's kraal reports under. The daily sync pulls every enabled farm; each
        farm's readings land on its own ranch. The API key is never entered here: it lives in Secret Manager and
        reaches the sync job through the env var named below.
      </p>

      {error && <p className="text-sm text-red-700 mb-3">{error.response?.data?.detail || 'Could not load the SmartKraal farm.'}</p>}
      {saveSuccess && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg flex items-center gap-2 text-sm">
          <Check className="h-4 w-4" /> SmartKraal farm saved
        </div>
      )}
      {saveError && <p className="text-sm text-red-700 mb-3">{saveError}</p>}

      {editing ? (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">SmartKraal farm id</label>
              <input type="number" min="1" step="1" value={farmId} onChange={(e) => setFarmId(e.target.value)}
                     className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" placeholder="e.g. 2" />
              <p className="text-xs text-gray-500 mt-1">
                The <code>a_idfarm</code> the SmartKraal team assigned. Cannot change once the mirror holds this farm's rows.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Promotion confidence floor</label>
              <input type="number" min="0" max="100" step="1" value={floor} onChange={(e) => setFloor(e.target.value)}
                     className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                     placeholder={data?.global_min_confidence_promote != null ? `blank = ${data.global_min_confidence_promote} (deployment)` : 'blank = deployment setting'} />
              <p className="text-xs text-gray-500 mt-1">Readings at or above it become weight records; blank uses the deployment's setting.</p>
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} className="rounded" />
            Pull this farm in the daily sync
          </label>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={1000}
                   className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                   placeholder="e.g. key issued 2026-11-10; kraal at the eastern dip" />
          </div>
          <div className="flex gap-3">
            <button onClick={() => { setEditing(false); setSaveError('') }}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm">Cancel</button>
            <button onClick={handleSave} disabled={saveMutation.isPending || !farmId}
                    className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 text-sm">
              <Save className="h-4 w-4" /> {saveMutation.isPending ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      ) : farm ? (
        <div className="p-4 bg-gray-50 rounded-lg">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-gray-500">Farm id</p>
              <p className="font-medium text-gray-900">{farm.farm_id}</p>
            </div>
            <div>
              <p className="text-gray-500">Daily pull</p>
              <p className={`font-medium ${farm.enabled ? 'text-green-700' : 'text-amber-700'}`}>{farm.enabled ? 'enabled' : 'disabled'}</p>
            </div>
            <div>
              <p className="text-gray-500">Promotion floor</p>
              <p className="font-medium text-gray-900">
                {farm.effective_min_confidence_promote == null
                  ? 'off (no floor set)'
                  : `≥ ${farm.effective_min_confidence_promote}${farm.min_confidence_promote == null ? ' (deployment)' : ''}`}
              </p>
            </div>
            <div>
              <p className="text-gray-500">Key expected in</p>
              <p className="font-mono text-xs text-gray-900">{farm.key_env}</p>
              {farm.fallback_key_applies && <p className="text-xs text-gray-500">falls back to {farm.key_env_fallback} (first configured farm)</p>}
            </div>
          </div>
          {farm.notes && <p className="text-xs text-gray-600 mt-3">{farm.notes}</p>}
        </div>
      ) : (
        <p className="text-gray-500 text-sm py-2">No SmartKraal farm is linked to this ranch.</p>
      )}
    </div>
  )
}

function FinanceApprovalConfig({ ranchId }) {
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [policy, setPolicy] = useState('first')
  const [selected, setSelected] = useState([])
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [saveError, setSaveError] = useState('')

  const { data: settings, isLoading, isError } = useQuery({
    queryKey: ['financeApprovalSettings', ranchId],
    queryFn: () => getFinanceApprovalSettings(ranchId),
    enabled: !!ranchId,
    retry: false, // 403 for non-owner/manager roles — just hide the card
  })

  useEffect(() => {
    // Don't clobber in-progress edits when the query refetches in the
    // background (window refocus, staleTime expiry).
    if (settings && !editing) {
      setPolicy(settings.policy || 'first')
      setSelected(settings.owners.filter(o => o.is_designated).map(o => o.user_id))
    }
  }, [settings, editing])

  const saveMutation = useMutation({
    mutationFn: () => updateFinanceApprovalSettings(ranchId, {
      policy,
      designatedApproverUserIds: selected,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries(['financeApprovalSettings', ranchId])
      setEditing(false)
      setSaveError('')
      setSaveSuccess(true)
      setTimeout(() => setSaveSuccess(false), 3000)
    },
    onError: (err) => {
      setSaveError(err.response?.data?.detail || 'Failed to save approval settings')
    },
  })

  if (isLoading || isError || !settings) return null

  const toggle = (userId) => {
    setSelected(prev => prev.includes(userId)
      ? prev.filter(id => id !== userId)
      : [...prev, userId])
  }

  const designatedNames = settings.owners
    .filter(o => o.is_designated)
    .map(o => o.full_name || o.email)

  return (
    <div className="bg-white rounded-lg shadow p-6 mt-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-blue-700" />
          <h2 className="text-lg font-semibold text-gray-900">Expense Approvals</h2>
        </div>
        {!editing && settings.can_edit && (
          <button
            onClick={() => setEditing(true)}
            className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
          >
            Edit
          </button>
        )}
      </div>

      <p className="text-sm text-gray-500 mb-4">
        Every expense must be approved by an owner/director before it counts in any
        report or ledger. Designate which owners act as approvers — undesignated owners
        won't be asked to vote. Leave all unticked to let any owner approve.
        {!settings.can_edit && ' Only owners can change these settings.'}
      </p>

      {saveSuccess && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg flex items-center gap-2 text-sm">
          <Check className="h-4 w-4" />
          Approval settings saved
        </div>
      )}
      {saveError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
          {saveError}
        </div>
      )}

      {editing ? (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Approval rule
            </label>
            <select
              value={policy}
              onChange={(e) => setPolicy(e.target.value)}
              className="w-full sm:w-96 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              {Object.entries(POLICY_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>

          <div>
            <p className="block text-sm font-medium text-gray-700 mb-2">Designated approvers</p>
            <div className="space-y-2">
              {settings.owners.map((o) => (
                <label key={o.user_id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selected.includes(o.user_id)}
                    onChange={() => toggle(o.user_id)}
                    className="h-4 w-4 text-blue-600 rounded"
                  />
                  <span className="text-sm text-gray-900">{o.full_name || o.email}</span>
                  <span className="text-xs text-gray-400">{o.email}</span>
                </label>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Untick everyone to allow any owner to approve (the default).
            </p>
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => {
                setEditing(false)
                setSaveError('')
                setPolicy(settings.policy || 'first')
                setSelected(settings.owners.filter(o => o.is_designated).map(o => o.user_id))
              }}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm"
            >
              Cancel
            </button>
            <button
              onClick={() => saveMutation.mutate()}
              disabled={saveMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-blue-700 text-white rounded-lg hover:bg-blue-800 disabled:opacity-50 text-sm"
            >
              <Save className="h-4 w-4" />
              {saveMutation.isPending ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      ) : (
        <div className="p-4 bg-gray-50 rounded-lg">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">Approval rule</p>
              <p className="font-medium text-gray-900">{POLICY_LABELS[settings.policy] || settings.policy}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Designated approvers</p>
              <p className="font-medium text-gray-900">
                {designatedNames.length > 0
                  ? designatedNames.join(', ')
                  : 'Any owner may approve'}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function BiocharConfig({ ranchId }) {
  const queryClient = useQueryClient()
  const [kilnCapacity, setKilnCapacity] = useState('')
  const [editing, setEditing] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)

  const { data: settings, isLoading } = useQuery({
    queryKey: ['biocharSettings', ranchId],
    queryFn: () => getBiocharSettings(ranchId),
    enabled: !!ranchId,
  })

  useEffect(() => {
    if (settings?.standard_kiln_capacity_kg) {
      setKilnCapacity(String(settings.standard_kiln_capacity_kg))
    }
  }, [settings])

  const saveMutation = useMutation({
    mutationFn: (data) => updateBiocharSettings(ranchId, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['biocharSettings', ranchId])
      setEditing(false)
      setSaveSuccess(true)
      setTimeout(() => setSaveSuccess(false), 3000)
    },
  })

  const handleSave = () => {
    const capacity = parseFloat(kilnCapacity)
    if (!capacity || capacity <= 0) return
    saveMutation.mutate({ standard_kiln_capacity_kg: capacity })
  }

  if (isLoading) return null

  return (
    <div className="bg-white rounded-lg shadow p-6 mt-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Flame className="h-5 w-5 text-orange-600" />
          <h2 className="text-lg font-semibold text-gray-900">Biochar Configuration</h2>
        </div>
        {!editing && (
          <button
            onClick={() => setEditing(true)}
            className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
          >
            Edit
          </button>
        )}
      </div>

      {saveSuccess && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg flex items-center gap-2 text-sm">
          <Check className="h-4 w-4" />
          Biochar settings saved successfully
        </div>
      )}

      {editing ? (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Standard Kiln Capacity (kg)
            </label>
            <input
              type="number"
              min="1"
              step="1"
              value={kilnCapacity}
              onChange={(e) => setKilnCapacity(e.target.value)}
              className="w-full sm:w-48 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
              placeholder="e.g., 200"
            />
            <p className="text-xs text-gray-500 mt-1">
              Maximum biomass input weight per batch. Used for automatic batch planning.
            </p>
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => {
                setEditing(false)
                setKilnCapacity(String(settings?.standard_kiln_capacity_kg || ''))
              }}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saveMutation.isPending || !kilnCapacity}
              className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 text-sm"
            >
              <Save className="h-4 w-4" />
              {saveMutation.isPending ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      ) : (
        <div className="p-4 bg-gray-50 rounded-lg">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">Standard Kiln Capacity</p>
              <p className="font-medium text-gray-900">
                {settings?.standard_kiln_capacity_kg ? `${settings.standard_kiln_capacity_kg} kg` : 'Not set'}
              </p>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-3">
            This determines how biomass is split into batches during batch planning.
          </p>
        </div>
      )}
    </div>
  )
}

function ConfigurationHistory({ ranchId }) {
  const navigate = useNavigate()

  const { data: configs, isLoading } = useQuery({
    queryKey: ['paddockConfigurations', ranchId],
    queryFn: () => getPaddockConfigurations(ranchId),
    enabled: !!ranchId,
  })

  if (isLoading || !configs || configs.length === 0) return null

  return (
    <div className="bg-white rounded-lg shadow p-6 mt-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <History className="h-5 w-5 text-purple-600" />
          <h2 className="text-lg font-semibold text-gray-900">Paddock Configuration History</h2>
        </div>
        <button
          onClick={() => navigate('/paddocks/reconfigure')}
          className="flex items-center gap-1 px-3 py-1.5 text-sm bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200"
        >
          <Layers className="h-4 w-4" />
          Reconfigure
        </button>
      </div>

      <div className="space-y-3">
        {configs.map((config) => (
          <div
            key={config.id}
            className={`flex items-center justify-between p-3 rounded-lg border ${
              config.is_active
                ? 'bg-green-50 border-green-200'
                : 'bg-gray-50 border-gray-200'
            }`}
          >
            <div className="flex items-center gap-3">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                config.is_active
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-300 text-gray-600'
              }`}>
                v{config.version}
              </div>
              <div>
                <p className="font-medium text-gray-900">
                  {config.name || `Configuration v${config.version}`}
                </p>
                <p className="text-sm text-gray-500">
                  {config.paddock_count || '?'} paddocks
                  {config.activated_at && (
                    <> &middot; {config.is_active ? 'Active since' : 'Used'} {new Date(config.activated_at).toLocaleDateString()}</>
                  )}
                  {config.deactivated_at && (
                    <> &ndash; {new Date(config.deactivated_at).toLocaleDateString()}</>
                  )}
                </p>
                {config.description && (
                  <p className="text-xs text-gray-400 mt-0.5">{config.description}</p>
                )}
              </div>
            </div>
            {config.is_active && (
              <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                Active
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
